// Mirrors impire-sdk-go/identity/wire.go — the identity verbs' wire the
// library already carries (re-exported here so that a surface imports one
// wire module per Go file) and the audit family the sign-in records are
// written on. A subject here is built by the library, never inlined.

import { identity, subject } from "@impire-io/sdk";

export type Carrier = identity.Carrier;
export type CarrierReply = identity.CarrierReply;
export type CarrierRequest = identity.CarrierRequest;
export type CreateTenantReply = identity.CreateTenantReply;
export type CreateTenantRequest = identity.CreateTenantRequest;
export type Membership = identity.Membership;
export type MembershipsReply = identity.MembershipsReply;

/** SERVICE is the auth service's micro service name. */
export const SERVICE = identity.SERVICE;

/** Role is the roles the platform fixes. */
export const Role = identity.Role;
export type Role = identity.Role;

/** The audit outcomes of a sign-in record: `audit.<tenant>.signin.<outcome>`. */
export const Outcome = identity.Outcome;
export type Outcome = identity.Outcome;

/** The refusal classes, as the audit record keeps them. */
export const Class = {
  claimShape: "claim-shape",
  platformState: "platform-state",
} as const;

/**
 * signinFilter is the subscription the admin template admits for the
 * tenant's sign-in records, and the member template for the member's own:
 * `audit.<slug>.signin.>`.
 */
// The tenant account imports the stream as `audit.<slug>.>` unrenamed and the
// admin template admits `audit.<slug>.signin.>` (impire-auth/internal/accounts).
export function signinFilter(slug: string): string {
  if (!subject.validSlug(slug)) {
    throw new Error(`identity: ${slug} is not a slug`);
  }
  return identity.auditFilter(slug);
}

/** outcomeOf reads the outcome back from a record's subject: the last token of its address; undefined for a subject not under the root. */
export function outcomeOf(subj: string): Outcome | undefined {
  let last: string | undefined;
  try {
    last = subject.address(subj).at(-1);
  } catch {
    return undefined;
  }
  return last === Outcome.placed || last === Outcome.refused || last === Outcome.ended ? last : undefined;
}

/**
 * ServerEnd is the server's reasons an `ended` record may carry that change
 * what the record says (02-the-auth-service.md § the audit records: the
 * server's own list, kept as the server wrote it). Every other reason —
 * "Client Closed" among them, which a reload, a closed tab and a reconnect
 * all leave — is a session that ended and nothing more.
 */
export const ServerEnd = {
  kicked: "Kicked",
  revoked: "Credentials Revoked",
  expired: "Authentication Expired",
} as const;

/** Ending is what an `ended` record says: signed out by the platform, expired, or a session that ended. */
export type Ending = "signed-out" | "expired" | "ended";

/**
 * endingOf reads an `ended` record's server reason: a sign-out only when the
 * platform signed the connection out — kicked, or its credentials revoked —
 * the sign-in's expiry when it expired, and otherwise a session that ended.
 * The reason's own text is never shown.
 */
export function endingOf(reason: string | undefined): Ending {
  switch (reason) {
    case ServerEnd.kicked:
    case ServerEnd.revoked:
      return "signed-out";
    case ServerEnd.expired:
      return "expired";
    default:
      return "ended";
  }
}
